"use client";

import { useEffect, useRef } from "react";

import { usePathname } from "next/navigation";

import {
  isPersonalizationPathMapEnabled,
  recordTimeSpent,
  runPathMapForUrl,
} from "@/lib/personalization/pathMap";

type PathMapTrackerProps = {
  apiUrl: string;
};

export function PathMapTracker({ apiUrl }: PathMapTrackerProps) {
  const pathname = usePathname() ?? "/";
  const pathMapValueRef = useRef<string | null>(null);
  const entryTimeRef = useRef(Date.now());

  useEffect(() => {
    if (!isPersonalizationPathMapEnabled(apiUrl)) return;

    recordTimeSpent(pathMapValueRef.current, entryTimeRef.current);
    pathMapValueRef.current = null;
    entryTimeRef.current = Date.now();

    const controller = new AbortController();
    const href = window.location.href;

    void runPathMapForUrl(apiUrl, href, controller.signal).then(
      (pathMapValue) => {
        if (controller.signal.aborted) return;
        pathMapValueRef.current = pathMapValue;
        entryTimeRef.current = Date.now();
      },
    );

    const flushCurrent = () => {
      if (!pathMapValueRef.current) return;
      recordTimeSpent(pathMapValueRef.current, entryTimeRef.current);
      pathMapValueRef.current = null;
      entryTimeRef.current = Date.now();
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        flushCurrent();
      }
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("pagehide", flushCurrent);

    return () => {
      controller.abort();
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("pagehide", flushCurrent);
    };
  }, [pathname, apiUrl]);

  return null;
}
